import { Phone, Mail, MapPin, ExternalLink, ChevronDown, CheckCircle2, MousePointerClick, ChefHat, PartyPopper } from "lucide-react";
import SEO, { breadcrumbSchema, cateringServiceSchema, faqSchema } from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import { restaurantInfo, cateringUrl } from "../mock";
import { useT, useLang } from "../context/LanguageContext";
import { CATERING_STRINGS } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const HERO_IMG =
  "https://static.wixstatic.com/media/ea1028_39f1ba77764c4096bd8347f0c54fce9b~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/catering-spread.jpg";

export const CATERING_FAQS = [
  {
    q: "How far in advance should I book catering?",
    qEs: "¿Con cuánta anticipación debo reservar el catering?",
    a: "We recommend ordering at least a few days ahead so we can source everything fresh. For weddings and large events, reach out one to two weeks in advance. You can place your order online anytime and get an instant quote.",
    aEs: "Recomendamos ordenar con algunos días de anticipación para conseguir todo fresco. Para bodas y eventos grandes, contáctanos con una o dos semanas de anticipación. Puedes ordenar en línea en cualquier momento y recibir una cotización instantánea.",
  },
  {
    q: "What areas do you cater in?",
    qEs: "¿En qué zonas hacen catering?",
    a: "We cater throughout Los Gatos, Saratoga, Campbell, Monte Sereno, San Jose, Cupertino, Santa Clara and the greater South Bay / Silicon Valley area.",
    aEs: "Hacemos catering en Los Gatos, Saratoga, Campbell, Monte Sereno, San Jose, Cupertino, Santa Clara y toda el área del South Bay / Silicon Valley.",
  },
  {
    q: "What catering formats do you offer?",
    qEs: "¿Qué formatos de catering ofrecen?",
    a: "Taco bars assembled on-site with your choice of proteins and salsas, pre-portioned burrito & bowl trays perfect for office lunches, and family-style spreads served just like at home in Mexico.",
    aEs: "Barras de tacos armadas en el lugar con tus proteínas y salsas favoritas, bandejas porcionadas de burritos y bowls ideales para oficinas, y servicio estilo familiar como en casa en México.",
  },
  {
    q: "Do you have vegetarian, vegan or gluten-free options?",
    qEs: "¿Tienen opciones vegetarianas, veganas o sin gluten?",
    a: "Yes. Our handmade corn tortillas are naturally gluten-free, and we offer vegetarian and vegan-friendly fillings, sides and salsas. Note any dietary needs when you order and we'll take care of your guests.",
    aEs: "Sí. Nuestras tortillas de maíz hechas a mano son naturalmente libres de gluten, y ofrecemos rellenos, guarniciones y salsas vegetarianas y aptas para veganos. Indica cualquier necesidad alimentaria al ordenar y cuidaremos a tus invitados.",
  },
  {
    q: "How do I get a quote?",
    qEs: "¿Cómo obtengo una cotización?",
    a: "Order directly on our online catering site for an instant, itemized quote — no waiting, no back-and-forth emails. Prefer to talk it through? Call us at (408) 402-5076 or email catering@taqueriadenina.com.",
    aEs: "Ordena directamente en nuestro sitio de catering en línea y recibe una cotización instantánea y detallada — sin esperas ni correos de ida y vuelta. ¿Prefieres hablarlo? Llámanos al (408) 402-5076 o escribe a catering@taqueriadenina.com.",
  },
  {
    q: "What events do you cater?",
    qEs: "¿Para qué eventos hacen catering?",
    a: "Weddings, corporate lunches and offsites, birthdays, quinceañeras, graduations, game days and backyard fiestas — if people are gathering, we'll feed them well.",
    aEs: "Bodas, almuerzos corporativos y offsites, cumpleaños, quinceañeras, graduaciones, días de partido y fiestas en el patio — si la gente se reúne, los alimentamos bien.",
  },
];

function CateringCTA({ source, big = false, children }) {
  return (
    <a
      href={cateringUrl(source)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("catering_click", { source })}
      data-testid={`catering-cta-${source}`}
      className={`inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-cream rounded-full font-semibold transition-all shadow-md hover:-translate-y-0.5 ${
        big ? "px-8 py-4 text-base" : "px-7 py-3.5 text-sm"
      }`}
    >
      {children} <ExternalLink className="w-4 h-4" />
    </a>
  );
}

export default function Catering() {
  const t = useT();
  const { lang } = useLang();
  const isEs = lang === "es";

  const schemas = [
    breadcrumbSchema([
      { name: isEs ? "Inicio" : "Home", path: "/" },
      { name: "Catering", path: "/catering" },
    ]),
    cateringServiceSchema,
    faqSchema(CATERING_FAQS, lang),
  ];

  const steps = [
    {
      icon: MousePointerClick,
      title: t("Build your order online", "Arma tu pedido en línea"),
      desc: t(
        "Pick your format, proteins, salsas and headcount. You get an instant, itemized quote — no waiting.",
        "Elige tu formato, proteínas, salsas y número de invitados. Recibes una cotización instantánea y detallada — sin esperas."
      ),
    },
    {
      icon: ChefHat,
      title: t("We cook it fresh that morning", "Lo cocinamos fresco esa mañana"),
      desc: t(
        "Tortillas pressed by hand, meats off the fire, salsas ground the day of your event. Nothing sits overnight.",
        "Tortillas hechas a mano, carnes recién salidas del fuego, salsas molidas el mismo día de tu evento. Nada se queda de un día para otro."
      ),
    },
    {
      icon: PartyPopper,
      title: t("You host, we handle the rest", "Tú recibes, nosotros nos encargamos"),
      desc: t(
        "Delivered ready to serve. Your only job is to accept the compliments.",
        "Entregado listo para servir. Tu único trabajo es recibir los cumplidos."
      ),
    },
  ];

  const cities = ["Los Gatos", "Saratoga", "Campbell", "Monte Sereno", "San Jose", "Cupertino", "Santa Clara"];

  return (
    <>
      <SEO
        title="Mexican Catering in Los Gatos & the South Bay — Taco Bars, Trays & More"
        titleEs="Catering Mexicano en Los Gatos y el South Bay — Barras de Tacos, Bandejas y Más"
        description="Authentic Mexican catering for weddings, corporate lunches & parties in Los Gatos, San Jose and the South Bay. Handmade tortillas, taco bars, burrito trays. Order online for an instant quote."
        descriptionEs="Catering mexicano auténtico para bodas, almuerzos corporativos y fiestas en Los Gatos, San Jose y el South Bay. Tortillas hechas a mano, barras de tacos, bandejas de burritos. Ordena en línea con cotización instantánea."
        path="/catering"
        image={HERO_IMG}
        keywords="Mexican catering Los Gatos, taco catering Bay Area, taco bar catering San Jose, wedding taco bar Los Gatos, corporate lunch catering Silicon Valley, Nina's Taqueria catering, burrito trays catering, quinceañera catering South Bay"
        keywordsEs="catering mexicano Los Gatos, catering de tacos Bay Area, barra de tacos San Jose, barra de tacos boda Los Gatos, almuerzo corporativo Silicon Valley, catering Nina's Taqueria, bandejas de burritos, catering quinceañera South Bay"
        schema={schemas}
      />
      <main className="pt-24 min-h-screen bg-cream-50">
        {/* Hero */}
        <section className="relative min-h-[55vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={HERO_IMG}
              alt="Mexican catering spread by Nina's Taqueria — taco bar catering in Los Gatos, California"
              className="w-full h-full object-cover ken-burns"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-granite-dark/90 via-granite-dark/60 to-granite-dark/15" />
            <div className="absolute inset-0 bg-gradient-to-t from-granite-dark/50 via-transparent to-transparent" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24 text-cream w-full">
            <div className="font-hand text-3xl text-brand-light mb-2">
              {t("Catering — Los Gatos & the South Bay", "Catering — Los Gatos y el South Bay")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-3xl">
              {t(
                "The taco bar your guests will talk about for weeks.",
                "La barra de tacos de la que tus invitados hablarán por semanas."
              )}
            </h1>
            <p className="mt-5 max-w-2xl text-cream/90 text-lg leading-relaxed">
              {t(
                "Handmade tortillas. Fire-kissed meats. Salsas ground the morning of your event. Weddings, office lunches and fiestas — catered the way Mexico intended.",
                "Tortillas hechas a mano. Carnes al fuego. Salsas molidas la mañana de tu evento. Bodas, almuerzos de oficina y fiestas — con el sabor que México manda."
              )}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <CateringCTA source="catering_hero" big>
                {t("Order Catering Online", "Ordena tu Catering en Línea")}
              </CateringCTA>
              <a
                href={`tel:${restaurantInfo.phone}`}
                className="inline-flex items-center gap-2 border border-cream/40 text-cream px-7 py-3.5 rounded-full text-sm font-medium hover:bg-cream hover:text-granite-dark transition-all"
                data-testid="catering-hero-phone"
              >
                <Phone className="w-4 h-4" /> {restaurantInfo.phone}
              </a>
            </div>
            <div className="mt-5 text-cream/70 text-sm">
              {t("Instant online quote · No deposit to browse", "Cotización instantánea en línea · Explora sin compromiso")}
            </div>
          </div>
        </section>

        {/* Formats */}
        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeading
              eyebrow={t(CATERING_STRINGS.expectEyebrow.en, CATERING_STRINGS.expectEyebrow.es)}
              title={t(CATERING_STRINGS.expectTitle.en, CATERING_STRINGS.expectTitle.es)}
              subtitle={t(
                "Three formats, one standard: everything made fresh, the day of your event.",
                "Tres formatos, un mismo estándar: todo hecho fresco, el día de tu evento."
              )}
            />
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {CATERING_STRINGS.cards.map((card, i) => (
                <div
                  key={i}
                  className="bg-cream rounded-3xl p-8 border border-cream-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
                  data-testid={`catering-format-card-${i}`}
                >
                  <div className="font-hand text-2xl text-brand mb-2">0{i + 1}</div>
                  <h3 className="font-display text-2xl font-semibold text-granite-dark">{t(card.title.en, card.title.es)}</h3>
                  <p className="mt-3 text-granite/70 text-[15px] leading-relaxed">{t(card.desc.en, card.desc.es)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 md:py-24 bg-granite-dark text-cream">
          <div className="max-w-7xl mx-auto px-6">
            <div className="font-hand text-3xl text-brand-light mb-2">{t("How it works", "Cómo funciona")}</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold max-w-2xl">
              {t("Ordering catering shouldn't feel like a second job.", "Contratar catering no debería sentirse como un segundo trabajo.")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              {steps.map((s, i) => (
                <div key={i} className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-brand/20 text-brand-light flex items-center justify-center mb-4">
                    <s.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-xl font-semibold">{s.title}</h3>
                  <p className="mt-2 text-cream/70 text-[15px] leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-12">
              <CateringCTA source="catering_how_it_works">
                {t("Get an Instant Quote", "Cotiza al Instante")}
              </CateringCTA>
            </div>
          </div>
        </section>

        {/* Areas served */}
        <section className="py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="flex items-center gap-3 shrink-0">
                <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center text-brand">
                  <MapPin className="w-5 h-5" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-granite-dark">
                  {t("We cater across the South Bay", "Hacemos catering en todo el South Bay")}
                </h2>
              </div>
              <div className="flex flex-wrap gap-2.5" data-testid="catering-areas-list">
                {cities.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 bg-cream border border-cream-100 text-granite-dark px-4 py-2 rounded-full text-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand" /> {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-cream">
          <div className="max-w-4xl mx-auto px-6">
            <SectionHeading
              eyebrow={t("Catering FAQ", "Preguntas frecuentes")}
              title={t("Everything you'd ask us on the phone.", "Todo lo que nos preguntarías por teléfono.")}
            />
            <div className="mt-10 space-y-3" data-testid="catering-faq-list">
              {CATERING_FAQS.map((f, i) => (
                <details key={i} className="group bg-cream-50 rounded-2xl border border-cream-100 overflow-hidden">
                  <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none font-display text-lg text-granite-dark hover:text-brand transition-colors">
                    {isEs ? f.qEs : f.q}
                    <ChevronDown className="w-5 h-5 text-brand shrink-0 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-6 pb-5 text-granite/75 text-[15px] leading-relaxed">{isEs ? f.aEs : f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-granite-dark rounded-3xl p-10 md:p-14 text-cream text-center relative overflow-hidden">
              <div className="font-hand text-3xl text-brand-light mb-2">{t("Let's feed your people", "Alimentemos a tu gente")}</div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold max-w-2xl mx-auto">
                {t("Your event, catered in minutes — not email threads.", "Tu evento, cotizado en minutos — no en cadenas de correos.")}
              </h2>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <CateringCTA source="catering_footer_cta" big>
                  {t("Start Your Catering Order", "Empieza tu Pedido de Catering")}
                </CateringCTA>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-cream/70">
                <a href={`mailto:${restaurantInfo.cateringEmail}`} className="inline-flex items-center gap-1.5 hover:text-brand-light">
                  <Mail className="w-4 h-4" /> {restaurantInfo.cateringEmail}
                </a>
                <a href={`tel:${restaurantInfo.phone}`} className="inline-flex items-center gap-1.5 hover:text-brand-light">
                  <Phone className="w-4 h-4" /> {restaurantInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
